import { Users } from "lucide-react";
import { useContent, useSettings } from "@/lib/api";
import { Chip, PageHero, Points, Reveal, Skeleton, Ticket, Wrap } from "@/night/ui";

export function Gangs() {
  const { data = [], isLoading } = useContent("gangs");
  const { data: s } = useSettings();
  return (
    <>
      <PageHero en="SYNDICATES" ar="باكيجات العصابات" desc="عصابات خاصة غير العامة: مقر خاص، هوية وملابس، مخزن ومركبات، وصلاحيات كاملة للقائد. تُسلَّم لصاحبها فقط." img="/site/gang-cartel.jpg" />
      {isLoading ? <Skeleton /> : (
        <div className="mt-16 space-y-4">
          {data.map((g, i) => (
            <Reveal key={g.id}>
              <article className="group relative isolate overflow-hidden border-y border-[var(--n-line)]" data-testid={`gang-${g.id}`}>
                <img src={g.image} alt={g.name} className="absolute inset-0 -z-20 h-full w-full object-cover opacity-35 transition-all duration-1000 group-hover:scale-105 group-hover:opacity-50" />
                <div className={`absolute inset-0 -z-10 ${i % 2 ? "bg-gradient-to-r" : "bg-gradient-to-l"} from-[#030509] via-[#030509]/85 to-transparent`} />
                <Wrap className={`flex py-16 ${i % 2 ? "justify-end" : ""}`}>
                  <div className="max-w-2xl">
                    <div className="f-lat text-7xl leading-none text-white/90 sm:text-8xl" dir="ltr" style={{ textAlign: "right" }}>{g.tag}</div>
                    <div className="mt-5 flex flex-wrap items-center gap-4">
                      <span className="f-lat text-5xl text-[var(--n-cyan)]" data-testid="gang-price">{g.price}</span>
                      <Chip tone="mute"><Users size={12} className="me-1.5" />{g.members}</Chip>
                    </div>
                    <p className="mt-5 leading-8 text-slate-300">{g.description}</p>
                    <Points items={g.features} className="mt-6 sm:columns-2" />
                  </div>
                </Wrap>
              </article>
            </Reveal>
          ))}
        </div>
      )}
      <Ticket href={s?.discord_invite} text="للطلب افتح تذكرة في الديسكورد ونكمل معك هناك." />
    </>
  );
}

const TIER = ["#c47a3a", "#cfd8e3", "#f5c84b", "#00e1ff"];

export function Packages() {
  const { data = [], isLoading } = useContent("packages");
  const { data: s } = useSettings();
  return (
    <>
      <PageHero en="PACKAGES" ar="الباكيجات" desc="ابدأ بقوة. اشترِ الباكج بمبلغ حقيقي، وتستلم المبلغ والمزايا على شخصيتك داخل المدينة مباشرة." />
      {isLoading ? <Skeleton /> : (
        <Wrap className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {data.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <article className={`relative h-full ${p.popular ? "holo lg:-translate-y-4" : ""}`} data-testid={`package-${p.id}`}>
                <div className="relative h-full overflow-hidden border border-[var(--n-line)] bg-[var(--n-ink)] p-8">
                  <div className="absolute inset-x-0 top-0 h-1" style={{ background: TIER[i % 4], boxShadow: `0 0 24px ${TIER[i % 4]}` }} />
                  <div className="flex items-center justify-between">
                    <span className="f-lat text-sm tracking-[0.3em]" style={{ color: TIER[i % 4] }}>TIER {["I", "II", "III", "IV", "V"][i]}</span>
                    {p.popular && <Chip>الأكثر طلباً</Chip>}
                  </div>
                  <h2 className="f-ar mt-8 text-3xl">{p.title}</h2>
                  <div className="f-lat mt-6 text-7xl leading-none text-white" data-testid="package-price">{p.realPrice}</div>
                  <div className="mt-6 border-y border-[var(--n-line)] py-4 text-lg font-bold text-emerald-300">{p.ingameMoney}</div>
                  <Points items={p.bonus} className="mt-6" />
                </div>
              </article>
            </Reveal>
          ))}
        </Wrap>
      )}
      <Ticket href={s?.discord_invite} text="للشراء افتح تذكرة في ديسكورد المدينة." />
    </>
  );
}

export function Businesses() {
  const { data = [], isLoading } = useContent("businesses");
  const { data: s } = useSettings();
  return (
    <>
      <PageHero en="BUSINESS" ar="البزنس والمشاريع" desc="ورشات، مطاعم، كافيهات، ومعارض. استأجر، وظّف، واكسب دخلك داخل المدينة." img="/site/biz-workshop.jpg" />
      {isLoading ? <Skeleton /> : (
        <Wrap className="mt-16 grid gap-px bg-[var(--n-line)] sm:grid-cols-2 lg:grid-cols-3">
          {data.map((b, i) => (
            <Reveal key={b.id} delay={(i % 3) * 0.06} className="bg-[var(--n-void)]">
              <article className="group h-full" data-testid={`biz-${b.id}`}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={b.image} alt={b.name} className="h-full w-full object-cover grayscale-[40%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                  <span className="absolute right-4 top-4"><Chip>{b.type}</Chip></span>
                </div>
                <div className="p-7">
                  <h2 className="f-ar text-2xl">{b.name}</h2>
                  <div className="mt-4 flex gap-6 text-sm"><span className="text-slate-400">استئجار: <b className="text-[var(--n-cyan)]">{b.rentPrice}</b></span><span className="text-slate-400">شراء: <b className="text-white">{b.buyPrice}</b></span></div>
                  <Points items={b.features} className="mt-5" />
                </div>
              </article>
            </Reveal>
          ))}
        </Wrap>
      )}
      <Ticket href={s?.discord_invite} />
    </>
  );
}
